import { NavLink, useNavigate } from "react-router-dom";
import { ReactComponent as UserGroupIcon } from "../../assets/icons/sidebar-users.svg";
import { ReactComponent as ImageGroupIcon } from "../../assets/icons/sidebar-images.svg";
import { ReactComponent as DashboardIcon } from "../../assets/icons/dashboardIcon.svg";
import { ReactComponent as BillingIcon } from "../../assets/icons/billing.svg";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as QuotaIcon } from "../../assets/icons/quota.svg";
import { ReactComponent as RoleGroupIcon } from "../../assets/icons/role.svg";

import { closeSidebar } from "../../data/slices/settingsSlice";
import { setHeaderTitle } from "../../data/slices/tabsSlice";
import { FaNetworkWired } from "react-icons/fa";
import { SiOpenaccess } from "react-icons/si";
import { RiPriceTag2Line } from "react-icons/ri";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { TbCpu } from "react-icons/tb";

import { useFlags } from "flagsmith/react";
import {
  ldap_auth_flag,
  lti_config_flag,
  quota_management_flag,
  network_policy_flag
} from "../../constants/flagsmith";

import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { AbilityContext } from "../../context/permissions/Can";

const NavigationLink = ({ icon = null, route = "/", children, title }) => {
  const dispatch = useDispatch();

  return (
    <NavLink
      to={route}
      className="navigation_link"
      onClick={() => {
        dispatch(closeSidebar());
        dispatch(setHeaderTitle(title));
      }}
      data-testid="navigation-link"
    >
      {icon}
      <span className="navigation_link__label">{children}</span>
    </NavLink>
  );
};

// SHOW_ALL_NAV — when true, every sidebar item renders unconditionally,
// regardless of CASL role or Flagsmith flag state. Flip to false to restore
// the original role/flag gating. We keep `useFlags`, `useContext` and the
// flag/role gates wired below so toggling is a single boolean flip.
const SHOW_ALL_NAV = true;

const Navigation = () => {
  // useFlags + AbilityContext stay called so React's hook order is stable
  // when SHOW_ALL_NAV is flipped back to false. ESLint pacification only —
  // values are unused while the override is on.
  // eslint-disable-next-line no-unused-vars
  const flags = useFlags([
    ldap_auth_flag,
    lti_config_flag,
    quota_management_flag,
    network_policy_flag
  ]);
  const { t } = useTranslation("dashboard");
  // eslint-disable-next-line no-unused-vars
  const ability = useContext(AbilityContext);
  // eslint-disable-next-line no-unused-vars
  const { profileOrg } = useSelector((state) => state.auth);

  // Per-item gates. When SHOW_ALL_NAV is on, every gate collapses to true.
  const showDashboard       = SHOW_ALL_NAV || ability.can("read", "stats");
  const showUsers           = SHOW_ALL_NAV || ability.can("read", "users");
  const showRoles           = SHOW_ALL_NAV || ability.can("read", "roles");
  const showImages          = SHOW_ALL_NAV || ability.can("read", "imagegroups");
  const showBilling         = SHOW_ALL_NAV || (ability.can("read", "subscriptions") && !profileOrg?.isFromMarketplace);
  const showPlans           = SHOW_ALL_NAV || (ability.can("read", "plans") && !profileOrg?.isFromMarketplace);
  const showLdap            = SHOW_ALL_NAV || (flags?.[ldap_auth_flag]?.enabled && ability.can("manage", "ldap"));
  const showLtiConfig       = SHOW_ALL_NAV || flags?.[lti_config_flag]?.enabled;
  const showNetworkPolicy   = SHOW_ALL_NAV || flags?.[network_policy_flag]?.enabled;
  const showQuotaManagement = SHOW_ALL_NAV || flags?.[quota_management_flag]?.enabled;
  const showInstanceCap     = SHOW_ALL_NAV || ability.can("read", "plans");

  return (
    <div className="navigation__container" data-testid="navigation-container">
      {showDashboard && (
        <NavigationLink title={t("dashboard-20")} route="/admin/dashboard" icon={<DashboardIcon />}>
          {t("dashboard-20")}
        </NavigationLink>
      )}

      {showUsers && (
        <NavigationLink title={t("dashboard-21")} route="/admin/users" icon={<UserGroupIcon />}>
          {t("dashboard-21")}
        </NavigationLink>
      )}

      {showRoles && (
        <NavigationLink
          title={t("dashboard-22")}
          route="/admin/user-groups"
          icon={<RoleGroupIcon />}
        >
          {t("dashboard-22")}
        </NavigationLink>
      )}

      {showImages && (
        <NavigationLink
          title={t("dashboard-23")}
          route="/admin/images"
          icon={<HiOutlineRectangleGroup color="#667085" />}
        >
          {t("dashboard-23")}
        </NavigationLink>
      )}

      {showBilling && (
        <NavigationLink title={t("dashboard-24")} route="/admin/billing" icon={<BillingIcon />}>
          {t("dashboard-24")}
        </NavigationLink>
      )}

      {showPlans && (
        <NavigationLink
          title={t("dashboard-26")}
          route="/admin/plans"
          icon={<RiPriceTag2Line color="#667085" />}
        >
          {t("dashboard-26")}
        </NavigationLink>
      )}

      {showLdap && (
        <NavigationLink
          title={t("dashboard-25")}
          route="/admin/ldap-config"
          icon={<FaNetworkWired color="#667085" />}
        >
          {t("dashboard-25")}
        </NavigationLink>
      )}

      {showLtiConfig && (
        <NavigationLink
          title={"Lti Configuration"}
          route="/admin/lti-config"
          icon={<FaNetworkWired color="#667085" />}
        >
          Lti Configuration
        </NavigationLink>
      )}

      {showNetworkPolicy && (
        <NavigationLink
          title={t("dashboard-27")}
          route="/admin/network-policy"
          icon={<SiOpenaccess color="#667085" />}
        >
          {t("dashboard-27")}
        </NavigationLink>
      )}

      {showQuotaManagement && (
        <NavigationLink
          title={t("dashboard-28")}
          route="/admin/quota-managment"
          icon={<QuotaIcon />}
        >
          {t("dashboard-28")}
        </NavigationLink>
      )}

      {showInstanceCap && (
        <NavigationLink
          title="Instance Capacity"
          route="/admin/capacity"
          icon={<TbCpu color="#667085" size={18} />}
        >
          Instance Capacity
        </NavigationLink>
      )}
    </div>
  );
};

export default Navigation;
